import { Badge, Separator } from '@monksflow/monks-ui';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import type { ExternalToolDto } from '@/entities/external-tool';

interface ExternalToolCardProps {
  externalTool: ExternalToolDto;
  onClick?: (externalTool: ExternalToolDto) => void;
}

export function ExternalToolCard({ externalTool, onClick }: ExternalToolCardProps) {
  const { downloadUrl: imageUrl } = useGetDownloadUrl(externalTool.imageAssetId);

  const handleCardClick = () => {
    if (externalTool.url) {
      window.open(externalTool.url, '_blank', 'noopener,noreferrer');
    }
    onClick?.(externalTool);
  };

  return (
    <button
      type="button"
      onClick={handleCardClick}
      className={`flex h-full min-h-62.5 flex-col gap-4 rounded-2xl border border-border bg-card p-6 text-left transition-shadow duration-base hover:shadow-md ${
        externalTool.url ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      {/* Categories and title share a fixed-height block so the divider lands at the same
          spot on every card in a row. */}
      <div className="flex min-h-30 flex-col gap-4">
        {externalTool.categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {externalTool.categories.map(
              (category) =>
                category.isVisible && (
                  <Badge key={category.id} variant="outline">
                    {category.title}
                  </Badge>
                ),
            )}
          </div>
        )}

        <div className="flex flex-1 items-start justify-between gap-4">
          <span className="flex-1 text-body-md font-medium text-foreground">
            {externalTool.title}
          </span>
          {imageUrl && (
            <img
              src={imageUrl}
              alt={externalTool.title}
              className="size-12 shrink-0 rounded-lg object-cover"
            />
          )}
        </div>
      </div>

      <Separator />

      <span className="line-clamp-3 text-body-sm text-muted-foreground">
        {externalTool.description}
      </span>
    </button>
  );
}
